import React, { useState } from 'react';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { useIntl } from 'react-intl';
import { Button, Checkbox, ComponentLevelNotification, Heading } from '@americanexpress/dls-react';
import CardMemberInfo from '../../CardMemberInfo';
import { Layout } from '../layout';
import { renderHTML } from '../../../../../utilities/render-html';
import useOneData from '../../../../../hooks/useOneData';
import useDisableButton from '../../../../../hooks/useDisableButton';
import {
  getEndpoint,
  getEnrollmentRequestBody,
} from '../../../../../api/api-utils';
import {
  CreateLoyaltyBenefitEnrollmentStatusVx,
  suppressCheckboxForLocaleList,
} from '../../../../../constants';

export function ClickAndGoApply(props) {
  const [checkbox, toggleCheckBox] = useState(false);
  const [agreeError, setAgreeError] = useState(false);
  const intl = useIntl(); // Hook for accessing locale content, intl.formatMessage()

  const stepTransition = props.stepTransition;
  const selectedProduct = props.selectedProduct;
  const label = props.callToAction?.INTERNAL?.label || props.callToAction?.ANCHOR?.label || 'Enroll Now';

  const description = props?.description;
  const safeDescription = renderHTML(description);

  // eslint-disable-next-line no-unsafe-optional-chaining -- needed
  const { isFetching, error, data } = props?.cardData;
  const languageData = props?.languageData;
  const faasApiUrl = props?.faasApiUrl;
  const sorBenefitId = props.sorBenefitId;
  const benefitName = props.benefitName;
  const cardname = props.cardname;
  const benefitTitle = props?.benefitTitle;
  const locale = props?.locale;
  const shouldSuppressCheckbox = suppressCheckboxForLocaleList.includes(locale);
  const endpoint = getEndpoint(
    faasApiUrl,
    CreateLoyaltyBenefitEnrollmentStatusVx
  );
  const requestBody = getEnrollmentRequestBody(
    selectedProduct,
    sorBenefitId,
    locale,
    { benefitName, cardname }
  );
  const endpointOptions = {
    body: requestBody,
    defer: true, // defers fetchye call until run function is invoked
  };
  const { isLoading, run } = useOneData(endpoint, endpointOptions);
  const { isDisabledAfterSubmit, disableButton } = useDisableButton();

  const parseEntities = (txt) => new DOMParser().parseFromString(txt, 'text/html').body.textContent;

  // eslint-disable-next-line consistent-return -- needed
  const submit = async () => {
    const isValidSubmit = checkbox;
    const showAgreeError = !checkbox;

    if (showAgreeError && !shouldSuppressCheckbox) {
      return setAgreeError(true);
    }

    if (shouldSuppressCheckbox || isValidSubmit) {
      setAgreeError(false);
      disableButton();
      const { data: enrollmentSuccess, error: enrollmentError } = await run();
      if (enrollmentSuccess) {
        stepTransition(true, false);
      }
      if (enrollmentError) {
        stepTransition(false, true);
      }
    }
  };

  const onChange = () => {
    if (!checkbox) {
      if (agreeError) {
        setAgreeError(false);
      }
      toggleCheckBox(true);
    } else {
      toggleCheckBox(false);
    }
  };

  return (
    <>
      <section className="text-justify">
        <Heading
          level={2}
          variant="sans-small-regular"
          className="text-align-center margin-3-b-lg-up margin-2-b pad-0"
        >
          {renderHTML(benefitTitle)}
        </Heading>
        {safeDescription && <article className="margin-3-b-lg-up margin-2-b">{safeDescription}</article>}
      </section>
      <Layout>
        {!isFetching && !error && (
          <CardMemberInfo {...data} languageData={languageData} />
        )}
        {shouldSuppressCheckbox || intl.formatMessage({
          id: 'hide-ui.tnc-checkbox',
          defaultMessage: '',
        }) === 'true'
          ? ''
          : (
            <section className="margin-t">
              <Checkbox
                id="enrollment-checkbox"
                label={intl.formatMessage({ id: 'shared.terms-links.terms-and-conditions', defaultMessage: 'I understand and agree to the terms and conditions.‡' })}
                reactlytics={{
                  element: `${cardname}>>${benefitName}>>agree-to-terms`,
                }}
                onChange={onChange}
              />
            </section>
          )}
        {agreeError && (
          <ComponentLevelNotification className="pad-2-t">
            Please read and agree to the Terms & Conditions.
          </ComponentLevelNotification>
        )}
        <ExtendReactlytics
          analytics={{
            event: {
              meta: {
                benefitname: `${parseEntities(benefitTitle)?.replace(/ /g, '-').toLowerCase()}`,
              },
            },
          }}
        >
          <Button
            id="enrollsubmit"
            className="width-full flex flex-column flex-align-items-center margin-auto-lr margin-2-t margin-1-b"
            reactlytics={{
              element: `cta>>enroll-submit>>${cardname?.replace(/ /g, '-').toLowerCase()}`,
              type: 'impression',
            }}
            onClick={submit}
            working={isLoading}
            aria-disabled={checkbox ? isDisabledAfterSubmit : null}
            aria-label={`${`${intl.formatMessage({ id: label })} ${benefitName}`}`}
            variant="primary"
          >
            {intl.formatMessage({
              id: `shared.terms-links.${label.replace(/ /g, '-').toLowerCase()}`,
              defaultMessage: label,
            })}
          </Button>
        </ExtendReactlytics>
      </Layout>
    </>
  );
}
